import { useDrag, useDrop } from 'react-dnd'

import CloseIcon from '@/assets/icons/icon-close.svg'
import { useHasChatUNreadMessages } from '@/models/chattingUI/chats'
import { useChattingTabs } from '@/modules/chatting/state/tabs-provider'
import type { ChattingTab } from '@/modules/chatting/types'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar'
import { classNames } from '@/utils/helpers'

import { IconButton } from '../../components/icon-button'
import { ToggleGroupItem } from '../../components/toggle-group'

interface Props {
  tab: ChattingTab
  width: number
  index: number
}

export const TabItem = ({ tab, width, index }: Props) => {
  const { activeTabIndex, closeTab, tabs, moveTabs } = useChattingTabs()
  const { title, fanId, avatar } = tab

  const [, dropRef] = useDrop({
    accept: 'tab',
    drop: (origin: ChattingTab) => moveTabs(origin, tab),
  })

  const [, dragRef, previewRef] = useDrag({
    item: () => tab,
    type: 'tab',
  })

  const isSmallVersion = width < 65
  const isCloseDisabled = index === 0 && tabs.length === 1
  const isSelected = activeTabIndex === index
  const isNextSelected = activeTabIndex === index + 1

  const closeButton = (
    <IconButton
      icon={CloseIcon}
      disabled={isCloseDisabled}
      onClick={(event) => {
        event.preventDefault()
        closeTab(index)
      }}
    />
  )

  const hasUnreadMessage = useHasChatUNreadMessages(tab.fanId)

  const tabIcon =
    fanId > 0 ? (
      <div
        className={classNames(
          'relative h-max w-max rounded-full',
          hasUnreadMessage && 'border-2 border-danger-default',
        )}
      >
        <CreatorAvatar
          avatarSrc={avatar}
          size={16}
          name={title}
          className="shrink-0"
          fallbackClassName="!text-[8px]"
        />
        {hasUnreadMessage && (
          <div className="bg-danger-default absolute -right-[2px] -top-[2px] h-2 w-2 rounded-full" />
        )}
      </div>
    ) : (
      <div className="h-4 w-4 shrink-0 rounded-full bg-secondary-400" />
    )

  return (
    <div
      ref={dropRef}
      className="flex flex-1 items-center overflow-hidden"
      style={{ width }}
    >
      <ToggleGroupItem
        value={String(index)}
        ref={(ref) => {
          dragRef(ref)
          previewRef(ref)
        }}
        asChild
      >
        <div
          className={classNames(
            'group/item relative flex h-[38px] w-max flex-1 items-center justify-between text-left hover:bg-primary-50 dark:hover:border-secondary-800 dark:hover:bg-secondary-800',
            isSmallVersion && 'justify-center',
            !isSmallVersion && 'px-2',
          )}
        >
          {isSmallVersion ? (
            <div className="flex items-center gap-2">
              {isSelected ? closeButton : tabIcon}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 overflow-hidden">
                {tabIcon}
                <div className="overflow-hidden text-clip whitespace-nowrap">
                  {title}
                </div>
              </div>
              <div className="relative">
                {closeButton}
                <div
                  className={classNames(
                    'absolute bottom-0 right-full top-0 w-4 bg-[linear-gradient(270deg,#FFF_0%,rgba(255,255,255,0.00)_100%)] dark:bg-[linear-gradient(270deg,hsl(var(--card))_0%,rgba(35,38,52,0)_100%)]',
                    isSelected
                      ? 'bg-[linear-gradient(90deg,rgba(238,247,255,0)_0%,#EEF7FF_93.63%)] dark:bg-[linear-gradient(90deg,rgba(6,26,45,0.00)_0%,hsl(var(--secondary-800))_93.63%)]'
                      : 'group-hover/item:bg-[linear-gradient(90deg,rgba(238,247,255,0)_0%,#EEF7FF_93.63%)] group-hover/item:dark:bg-[linear-gradient(90deg,rgba(6,26,45,0.00)_0%,hsl(var(--secondary-800))_93.63%)]',
                  )}
                />
              </div>
            </>
          )}
        </div>
      </ToggleGroupItem>
      <div
        className={classNames('ml-0.5 h-6 shrink-0 border-r', {
          'border-transparent': isSelected || isNextSelected,
        })}
      />
    </div>
  )
}
